/**
 * Accessible, horizontally scrolling tab strip. Arrow keys move between tabs;
 * the strip never wraps, so seven tabs still fit at 375px by scrolling.
 */
import { useId, type KeyboardEvent } from 'react';

export interface TabItem<K extends string> {
  key: K;
  label: string;
  count?: number;
}

export function TabStrip<K extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
}: {
  tabs: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
  /** Pass the same prefix to TabPanel so aria-controls / aria-labelledby line up. */
  idPrefix?: string;
}) {
  const generated = useId();
  const prefix = idPrefix ?? generated;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((t) => t.key === value);
    if (index === -1) return;
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    onChange(tabs[next].key);
    const el = document.getElementById(tabId(prefix, tabs[next].key));
    el?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className="tab-strip" onKeyDown={onKeyDown}>
      {tabs.map((t) => {
        const selected = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={tabId(prefix, t.key)}
            aria-selected={selected}
            aria-controls={panelId(prefix, t.key)}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.key)}
            className="tab-btn"
          >
            {t.label}
            {typeof t.count === 'number' && (
              <span className="ml-1.5 rounded-full bg-surface-3 px-1.5 py-0.5 text-[11px] font-semibold text-text-muted">
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function tabId(prefix: string, key: string): string {
  return `${prefix}-tab-${key}`;
}

export function panelId(prefix: string, key: string): string {
  return `${prefix}-panel-${key}`;
}

export function TabPanel({
  prefix,
  tabKey,
  children,
  className,
}: {
  prefix: string;
  tabKey: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div role="tabpanel" id={panelId(prefix, tabKey)} aria-labelledby={tabId(prefix, tabKey)} tabIndex={0} className={`focus:outline-none ${className ?? ''}`}>
      {children}
    </div>
  );
}
